(() => {
  const isMobile = () => window.matchMedia("(max-width: 767px)").matches;
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const buildDots = (holder, items, onSelect) => {
    if (!holder || holder.children.length) return [...(holder?.children || [])];

    return items.map((item, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.setAttribute("aria-label", `Go to slide ${index + 1}`);
      button.addEventListener("click", () => onSelect(index));
      holder.appendChild(button);
      return button;
    });
  };

  const initNavigation = (root) => {
    if (!root || root.dataset.ready === "true") return;
    root.dataset.ready = "true";

    const links = [...root.querySelectorAll(".konsoe-about-nav__link")];
    const targets = links
      .map((link) => document.querySelector(link.getAttribute("href")))
      .filter(Boolean);

    if (!("IntersectionObserver" in window) || !targets.length) return;

    const observer = new IntersectionObserver((entries) => {
      const current = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

      if (!current) return;

      links.forEach((link) => {
        link.classList.toggle(
          "is-active",
          link.getAttribute("href") === `#${current.target.id}`
        );
      });
    }, {
      rootMargin: "-15% 0px -70% 0px",
      threshold: [0, .1, .35]
    });

    targets.forEach((target) => observer.observe(target));
  };

  const initStories = (root) => {
    if (!root || root.dataset.ready === "true") return;
    root.dataset.ready = "true";

    const track = root.querySelector("[data-story-track]");
    const slides = track ? [...track.querySelectorAll("[data-story-slide]")] : [];
    const dots = buildDots(
      root.querySelector("[data-story-dots]"),
      slides,
      (index) => {
        track.scrollTo({
          left: slides[index].offsetLeft,
          behavior: reduceMotion ? "auto" : "smooth"
        });
      }
    );

    const update = () => {
      if (!track || !slides.length) return;
      const index = Math.max(
        0,
        Math.min(slides.length - 1, Math.round(track.scrollLeft / track.clientWidth))
      );
      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle("is-active", dotIndex === index);
      });
    };

    if (track) track.addEventListener("scroll", update, { passive: true });
    update();
  };

  const initTimeline = (root) => {
    if (!root || root.dataset.ready === "true") return;
    root.dataset.ready = "true";

    const timelineWindow = root.querySelector("[data-timeline-window]");
    const track = root.querySelector("[data-timeline-track]");
    const slides = track ? [...track.querySelectorAll("[data-timeline-slide]")] : [];
    const yearPrev = root.querySelector("[data-mobile-year-prev]");
    const yearCurrent = root.querySelector("[data-mobile-year-current]");
    const yearNext = root.querySelector("[data-mobile-year-next]");
    const loadMore = root.querySelector("[data-timeline-load-more]");
    let index = 0;
    let visibleCount = Math.min(3, slides.length);

    const dots = buildDots(root.querySelector("[data-timeline-dots]"), slides, (newIndex) => {
      index = newIndex;
      update();
    });

    const updateYear = () => {
      if (!yearCurrent || !slides.length) return;
      const previousIndex = (index - 1 + slides.length) % slides.length;
      const nextIndex = (index + 1) % slides.length;
      const previousText = yearPrev?.querySelector("[data-mobile-year-text]");
      const currentText = yearCurrent.querySelector("[data-mobile-year-text]");
      const nextText = yearNext?.querySelector("[data-mobile-year-text]");

      if (previousText) previousText.textContent = slides[previousIndex].dataset.year || "";
      if (currentText) currentText.textContent = slides[index].dataset.year || "";
      if (nextText) nextText.textContent = slides[nextIndex].dataset.year || "";
    };

    const update = () => {
      if (!track || !slides.length) {
        if (loadMore) loadMore.hidden = true;
        return;
      }

      if (isMobile()) {
        index = (index + slides.length) % slides.length;
        track.style.transform = `translateX(-${index * 100}%)`;
        if (timelineWindow) timelineWindow.style.height = "184px";
        if (loadMore) loadMore.hidden = true;
      } else {
        index = 0;
        visibleCount = Math.max(1, Math.min(slides.length, visibleCount));
        track.style.transform = "none";
        if (timelineWindow) timelineWindow.style.height = `${visibleCount * 553}px`;
        if (loadMore) {
          const allVisible = visibleCount >= slides.length;
          loadMore.hidden = allVisible;
          loadMore.setAttribute("aria-expanded", String(allVisible));
        }
      }

      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle("is-active", slideIndex === index);
      });
      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle("is-active", dotIndex === index);
      });
      updateYear();
    };

    yearPrev?.addEventListener("click", () => {
      if (!slides.length) return;
      index -= 1;
      update();
    });

    yearNext?.addEventListener("click", () => {
      if (!slides.length) return;
      index += 1;
      update();
    });

    loadMore?.addEventListener("click", () => {
      visibleCount = Math.min(slides.length, visibleCount + 3);
      update();
    });

    let touchStartX = null;
    let touchStartY = null;

    root.addEventListener("touchstart", (event) => {
      if (!isMobile() || !event.touches.length) return;
      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
    }, { passive: true });

    root.addEventListener("touchend", (event) => {
      if (
        !isMobile() ||
        touchStartX === null ||
        !event.changedTouches.length ||
        !slides.length
      ) return;

      const deltaX = event.changedTouches[0].clientX - touchStartX;
      const deltaY = event.changedTouches[0].clientY - touchStartY;
      touchStartX = null;
      touchStartY = null;

      if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      index += deltaX < 0 ? 1 : -1;
      update();
    }, { passive: true });

    window.addEventListener("resize", update, { passive: true });
    update();
  };

  const initEvents = (root) => {
    if (!root || root.dataset.ready === "true") return;
    root.dataset.ready = "true";

    const eventWindow = root.querySelector("[data-event-window]");
    const track = root.querySelector("[data-event-track]");
    const slides = track ? [...track.querySelectorAll("[data-event-slide]")] : [];
    let index = slides.length > 1 ? 1 : 0;

    const update = () => {
      if (!track || !eventWindow || !slides.length) return;
      index = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle("is-active", slideIndex === index);
      });

      requestAnimationFrame(() => {
        const active = slides[index];
        const target = active.offsetLeft + active.offsetWidth / 2 - eventWindow.clientWidth / 2;
        track.scrollTo({
          left: Math.max(0, target),
          behavior: reduceMotion ? "auto" : "smooth"
        });
      });
    };

    root.querySelector("[data-event-prev]")?.addEventListener("click", () => {
      index -= 1;
      update();
    });

    root.querySelector("[data-event-next]")?.addEventListener("click", () => {
      index += 1;
      update();
    });

    slides.forEach((slide, slideIndex) => {
      slide.addEventListener("click", () => {
        index = slideIndex;
        update();
      });
    });

    window.addEventListener("resize", update, { passive: true });
    update();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll("[data-konsoe-about-nav]").forEach(initNavigation);
    scope.querySelectorAll("[data-konsoe-stories]").forEach(initStories);
    scope.querySelectorAll("[data-konsoe-timeline]").forEach(initTimeline);
    scope.querySelectorAll("[data-konsoe-events]").forEach(initEvents);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });
})();
